import { useEffect, useRef } from 'react'

import { fetchVimeoThumbnail } from '@/helpers/video/videoThumbnails'

import type { MediaType, VideoType } from './types'

const processVimeoThumbnail = async (
  videoId: string,
  onMediaUpdate: (updater: (prev: MediaType[]) => MediaType[]) => void
) => {
  const thumbnailUrl = await fetchVimeoThumbnail(videoId)

  if (thumbnailUrl) {
    // Update media with real thumbnail for all sizes
    onMediaUpdate((prev) =>
      prev.map((item) =>
        item.type === 'video' && item.videoData.videoId === videoId
          ? {
              ...item,
              urls: {
                large: thumbnailUrl,
                medium: thumbnailUrl,
                thumb: thumbnailUrl,
                original: thumbnailUrl,
              },
            }
          : item
      )
    )
  } else {
    // Remove video if fetch fails (invalid/private/deleted)
    onMediaUpdate((prev) =>
      prev.filter(
        (item) => !(item.type === 'video' && item.videoData.videoId === videoId)
      )
    )
  }
}

export const useAsyncThumbnails = (
  allMedia: MediaType[],
  enableVideoInGallery: boolean,
  onMediaUpdate: (updater: (prev: MediaType[]) => MediaType[]) => void
) => {
  const processedVideoIds = useRef<Set<string>>(new Set())

  useEffect(() => {
    if (!enableVideoInGallery) return

    const vimeoVideos = allMedia.filter(
      (item): item is VideoType =>
        item.type === 'video' && item.videoData.platform === 'vimeo'
    )

    if (vimeoVideos.length === 0) return

    // Fetch real Vimeo thumbnails for videos we haven't processed yet
    vimeoVideos.forEach((videoItem) => {
      const videoId = videoItem.videoData.videoId

      // Skip if we've already started fetching this video
      if (processedVideoIds.current.has(videoId)) return

      // Mark as processed to avoid duplicate fetches
      processedVideoIds.current.add(videoId)

      // Fire and forget async operation
      void processVimeoThumbnail(videoId, onMediaUpdate)
    })
  }, [allMedia, enableVideoInGallery, onMediaUpdate])
}
